import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ContactDetails } from "@/features/person/components/ContactDetails";
import FacilitySideSheet from "@/features/facility/components/FacilitySideSheet";
import { Gender, type Person, type Address, type PhoneNumber } from "@/types";
import type { UpdatedFields } from "@/features/person/components/ContactDetails";
import { useNavigate } from "@/router";
import { scrollToElement } from "@/lib/scrollHelpers";
import { formatPhoneNumber } from "@/lib/phoneHelpers";
import type { MembershipApplicationDetail } from "./queries";
import { useApplicationContext } from "./useApplicationContext";
import ApplicantPhotoSection from "./_components/ApplicantPhotoSection";
import BeneficiariesSection from "./_components/BeneficiariesSection";
import AcknowledgementsSection from "./_components/AcknowledgementsSection";
import CriminalHistoryDocumentsSection from "./_components/CriminalHistoryDocumentsSection";
import EducationSection from "./_components/EducationSection";
import EducationDocumentsSection from "./_components/EducationDocumentsSection";
import AssociateEmploymentSection from "./_components/AssociateEmploymentSection";
import MemberApplicationEmploymentSection from "./_components/MemberApplicationEmploymentSection";
import ExperienceCreditsToBeAppliedSection from "./_components/ExperienceCreditsToBeAppliedSection";
import {
  APPLICATION_CRIMINAL_HISTORY_SCROLL_TARGET,
  APPLICATION_EDUCATION_SCROLL_TARGET,
  APPLICATION_EMPLOYMENT_SCROLL_TARGET,
  requiresEducationDocumentationReview
} from "./utils";

const GENDER_MAP: Record<string, Gender> = {
  male: Gender.Male,
  female: Gender.Female,
  other: Gender.Other
};

// `email` is still computed client-side because the backend write-back does not yet sync email.
function buildUpdatedFields(app: MembershipApplicationDetail): UpdatedFields {
  const { updatedFields, person } = app;
  const emailChanged = !!app.email && !!person.email && app.email.toLowerCase() !== person.email.toLowerCase();

  return {
    mobilePhone: updatedFields.mobilePhone,
    homePhone: updatedFields.homePhone,
    homeAddress: updatedFields.homeAddress,
    directoryOptOut: updatedFields.directoryOptOut,
    email: emailChanged
  };
}

export default function ApplicationDetailsPage() {
  const { application } = useApplicationContext();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [viewEmploymentId, setViewEmploymentId] = useState<string | null>(null);
  const [viewFacilityId, setViewFacilityId] = useState<string | null>(null);

  useEffect(() => {
    const scrollTo = searchParams.get("scrollTo");
    if (!scrollTo) return;

    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.delete("scrollTo");
    window.history.replaceState({}, "", currentUrl.toString());

    requestAnimationFrame(() => {
      scrollToElement(scrollTo);
    });
  }, [searchParams]);

  if (!application) return null;

  const phoneNumbers: PhoneNumber[] = [];
  if (application.mobilePhone) {
    phoneNumbers.push({
      id: "mobile",
      phoneType: "mobile",
      phoneNumber: application.mobilePhone,
      phoneFormatted: formatPhoneNumber(application.mobilePhone),
      phoneCountry: "US"
    });
  }
  if (application.homePhone) {
    phoneNumbers.push({
      id: "home",
      phoneType: "home",
      phoneNumber: application.homePhone,
      phoneFormatted: formatPhoneNumber(application.homePhone),
      phoneCountry: "US"
    });
  }

  const addresses: Address[] = [];
  if (application.address1) {
    addresses.push({
      id: "home",
      addressType: "home",
      address1: application.address1,
      address2: application.address2 ?? "",
      city: application.city ?? "",
      state: application.state ?? "",
      postalCode: application.postalCode ?? "",
      countryCode: application.country ?? "US",
      isVerified: false
    });
  }

  const { person: crmPerson } = application;
  const isMemberApplication = application.applicationType === "member";
  const isAssociateApplication = application.applicationType === "associate";
  const requiresEducationReview = requiresEducationDocumentationReview(application.applicationType);

  const person: Person = {
    // Application-submitted fields
    firstName: application.firstName ?? "",
    lastName: application.lastName ?? "",
    email: application.email ?? undefined,
    informalName: application.informalName ?? undefined,
    dob: application.dateOfBirth
      ? (() => {
          const [year, month, day] = application.dateOfBirth!.split("-").map(Number);
          return { year, month, day };
        })()
      : undefined,
    gender: application.gender ? GENDER_MAP[application.gender.toLowerCase()] : undefined,
    spouseName: application.spousePartnerName,
    directoryOptOut: application.directoryOptOut,
    memberCardFulfillment: application.memberCardFulfillment,
    spouseCardFulfillment: application.spouseCardFulfillment,
    phoneNumbers,
    addresses,

    // CRM person fields (no application equivalent)
    id: crmPerson.pgaId,
    displayName: crmPerson.displayName,
    age: crmPerson.age ?? undefined,
    suffixName: crmPerson.suffixName ?? undefined,
    middleName: crmPerson.middleName ?? undefined,
    preferredLastName: crmPerson.preferredLastName ?? undefined,
    ssn: crmPerson.ssn ?? undefined,
    contactAddressId: crmPerson.contactAddressId ?? undefined,
    primarySection: crmPerson.primarySection ?? undefined,
    primarySectionAffiliation: crmPerson.primarySectionAffiliation as Person["primarySectionAffiliation"],
    latestProgram: crmPerson.latestProgram as Person["latestProgram"],
    militaryService: crmPerson.militaryService ?? undefined,

    // Conditional CRM fields
    employments:
      application.applicationType === "member"
        ? ((application.backofficeEmployments ?? []) as unknown as Person["employments"])
        : []
  };

  return (
    <>
      <FacilitySideSheet
        open={!!viewEmploymentId || !!viewFacilityId}
        onClose={() => {
          setViewEmploymentId(null);
          setViewFacilityId(null);
        }}
        employmentId={viewEmploymentId ?? undefined}
        facilityId={viewFacilityId}
      />

      <ContactDetails person={person} updatedFields={buildUpdatedFields(application)} suppressInlineEditing />

      {application.profilePhotoUrl && application.profilePhotoUrl !== application.person.profilePhoto && (
        <ApplicantPhotoSection application={application} />
      )}
      {requiresEducationReview && (
        <>
          <EducationSection
            applicationId={application.id}
            educationDetails={application.educationDetails}
            status={application.status}
          />
          <div id={APPLICATION_EDUCATION_SCROLL_TARGET} data-testid={APPLICATION_EDUCATION_SCROLL_TARGET}>
            <EducationDocumentsSection
              applicationId={application.id}
              status={application.status}
              documents={application.educationDocuments}
              eligibilityStatuses={application.eligibilityStatuses}
            />
          </div>
        </>
      )}
      {isAssociateApplication && (
        <>
          <div id={APPLICATION_EMPLOYMENT_SCROLL_TARGET} data-testid={APPLICATION_EMPLOYMENT_SCROLL_TARGET}>
            <AssociateEmploymentSection
              applicationId={application.id}
              status={application.status}
              currentEmploymentDetails={application.currentEmploymentDetails}
              previousEmploymentDetails={application.previousEmploymentDetails}
              jobDescriptionDocuments={application.jobDescriptionDocuments}
              eligibilityStatuses={application.eligibilityStatuses}
              onViewFacility={setViewFacilityId}
            />
          </div>
          <ExperienceCreditsToBeAppliedSection experienceCredits={application.experienceCredits} />
        </>
      )}
      {isMemberApplication && (
        <div id={APPLICATION_EMPLOYMENT_SCROLL_TARGET} data-testid={APPLICATION_EMPLOYMENT_SCROLL_TARGET}>
          <MemberApplicationEmploymentSection
            applicationId={application.id}
            pgaId={crmPerson.pgaId}
            status={application.status}
            employments={application.backofficeEmployments ?? []}
            onViewFacility={(facilityId) => navigate("/facilities/:facilityId", { params: { facilityId } })}
            onClickEmployment={setViewEmploymentId}
          />
        </div>
      )}
      <BeneficiariesSection
        primaryBeneficiaries={application.primaryBeneficiaryInfo}
        contingentBeneficiaries={application.contingentBeneficiaryInfo}
      />
      <AcknowledgementsSection application={application} />
      {application.hasCriminalHistory && (
        <div id={APPLICATION_CRIMINAL_HISTORY_SCROLL_TARGET} data-testid={APPLICATION_CRIMINAL_HISTORY_SCROLL_TARGET}>
          <CriminalHistoryDocumentsSection
            applicationId={application.id}
            status={application.status}
            documents={application.criminalHistoryDocuments}
            eligibilityStatuses={application.eligibilityStatuses}
          />
        </div>
      )}
    </>
  );
}
